import { Helmet } from "react-helmet";
import { DashboardLayout } from "../../../../components/layouts/DashboardLayout";
import { Header } from "../../../../components/Header";
import { useState } from "react";
import { useHookInvoiceGroup } from "../../../../hooks/useHookInvoiceGroup";
import { useNavigate } from "react-router-dom";
import {
  FaChevronRight,
  FaChevronLeft,
  FaAnglesLeft,
  FaAnglesRight,
  FaCreditCard,
  FaHashtag,
  FaMagnifyingGlass,
  FaFilter,
  FaUser,
  FaFileInvoiceDollar,
  FaPlus,
} from "react-icons/fa6";
import { SelectNas } from "../../../../components/forms/selects/SelectNas";
import { SelectPop } from "../../../../components/forms/selects/SelectPop";
import { SelectOdp } from "../../../../components/forms/selects/SelectOdp";
import { formatRupiah } from "../../../../utilities/formatRupiah";
import { BtnDefault } from "../../../../components/buttons/BtnDefault";
import { SelectOdc } from "../../../../components/forms/selects/SelectOdc";

/* ============================================
   KOMPONEN: Skeleton
   ============================================ */
const SkeletonRow = ({ cols = 6 }) => (
  <tr className="animate-pulse">
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i} className="px-4 py-3">
        <div
          className="h-3.5 bg-slate-700/60 rounded-md"
          style={{ width: `${40 + Math.random() * 50}%` }}
        />
      </td>
    ))}
  </tr>
);

const SkeletonCard = () => (
  <div
    className="rounded-2xl overflow-hidden animate-pulse"
    style={{
      background: "rgba(15, 23, 42, 0.5)",
      border: "1px solid rgba(51, 65, 85, 0.3)",
    }}
  >
    <div
      className="px-4 py-3 flex justify-between items-center"
      style={{ background: "rgba(15, 23, 42, 0.7)", borderBottom: "1px solid rgba(51, 65, 85, 0.2)" }}
    >
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-lg bg-slate-700/60" />
        <div className="h-3 bg-slate-700/50 rounded-md w-20" />
      </div>
      <div className="h-6 bg-slate-700/40 rounded-full w-14" />
    </div>
    <div className="p-4">
      <div className="flex justify-between items-center mb-3">
        <div className="space-y-2">
          <div className="h-5 bg-slate-700/60 rounded-md w-32" />
          <div className="h-3 bg-slate-700/30 rounded-md w-24" />
        </div>
        <div className="text-right space-y-1">
          <div className="h-2.5 bg-slate-700/30 rounded w-10 ml-auto" />
          <div className="h-6 bg-slate-700/50 rounded-md w-24 ml-auto" />
        </div>
      </div>
      <div
        className="flex justify-between items-center pt-3"
        style={{ borderTop: "1px solid rgba(51, 65, 85, 0.15)" }}
      >
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded bg-slate-700/50" />
          <div className="h-3 bg-slate-700/30 rounded w-10" />
        </div>
        <div className="h-3 bg-slate-700/30 rounded w-16" />
      </div>
    </div>
  </div>
);

/* ============================================
   KOMPONEN: Status Badge
   ============================================ */
const StatusBadge = ({ status }) => {
  const isActive = status === "aktif" || status === "lunas" || status === "paid";
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold tracking-wide ${isActive
          ? "bg-emerald-500/12 text-emerald-400 border border-emerald-500/20"
          : "bg-red-500/12 text-red-400 border border-red-500/20"
        }`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-emerald-400" : "bg-red-400"
          }`}
      />
      {status || "-"}
    </span>
  );
};

/* ============================================
   KOMPONEN: Invoice
   ============================================ */
export const InvoiceGroup = () => {
  const [showSidebar, setShowSidebar] = useState(true);
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState([]);

  const {
    data,
    meta,
    loading,
    params,
    setParams,
    refreshData,
  } = useHookInvoiceGroup();

  /* ---------- Selection ---------- */
  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === data?.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(data?.map((item) => item.id));
    }
  };

  /* ---------- Pagination ---------- */
  const getPages = () => {
    if (!meta) return [];
    const total = meta.last_page;
    const current = meta.current_page;
    const delta = 2;

    const pages = [];
    const left = Math.max(2, current - delta);
    const right = Math.min(total - 1, current + delta);

    pages.push(1);
    if (left > 2) pages.push("...");
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < total - 1) pages.push("...");
    if (total > 1) pages.push(total);

    return pages;
  };

  /* ---------- Column Config ---------- */
  const columns = [
    { key: "no", label: "No", align: "center", width: "60px" },
    // { key: "status", label: "Status", align: "left", },
    { key: "internet_number", label: "No Internet", align: "left" },
    { key: "name", label: "Nama", align: "left" },
    { key: "tagihan", label: "Total Tagihan", align: "left" },
    { key: "total_pembayaran", label: "Sudah Dibayar", align: "left" },
    { key: "total_tagihan", label: "Sisa Tagihan", align: "left" },
    { key: "pop", label: "Pop", align: "left" },
    { key: "odc", label: "Odc", align: "left" },
    { key: "odp", label: "Odp", align: "left" },
  ];

  return (
    <>
      <Helmet>
        <title>Invoice - DANELA NET</title>
        <meta
          name="description"
          content="Kelola data invoice tagihan pelanggan, pantau status pembayaran, dan filter berdasarkan wilayah."
        />
      </Helmet>

      <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
        {/* ===== Header ===== */}
        <Header toggleSidebar={() => setShowSidebar(!showSidebar)}>
          <div className="flex items-center gap-3">
            <h2 className="text-base md:text-xl font-bold text-gray-100">
              Invoice Tagihan
            </h2>
            {meta && (
              <span className="hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                {meta.total} data
              </span>
            )}
          </div>
          <BtnDefault onClick={() => navigate("/dashboard/invoice/create")}>
            <FaPlus className="text-xs hidden sm:block" />
            <span>Tambah Data</span>
          </BtnDefault>
        </Header>

        {/* ===== Content ===== */}
        <main className="p-4 lg:p-6 space-y-5">
          {/* ===== Search & Filter ===== */}
          <section
            className="rounded-2xl p-4 sm:p-5"
            style={{
              background: "rgba(15, 23, 42, 0.5)",
              border: "1px solid rgba(51, 65, 85, 0.3)",
              backdropFilter: "blur(12px)",
            }}
            aria-label="Filter dan pencarian"
          >
            <div className="flex flex-col lg:flex-row lg:items-end gap-4">
              {/* Search */}
              <div className="w-full lg:max-w-xs flex-shrink-0">
                <label htmlFor="search-pppoe" className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">
                  Cari Data
                </label>
                <div className="relative">
                  <FaMagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm" />
                  <input
                    id="search-pppoe"
                    type="text"
                    placeholder="Nama, ID Pelanggan..."
                    value={params?.search}
                    onChange={(e) => setParams({ ...params, search: e.target.value })}
                    className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 placeholder-slate-500 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all"
                  />
                </div>
              </div>

              {/* Filters */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1.5">
                  <FaFilter className="text-xs text-slate-500" />
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Filter</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">

                  <SelectNas
                    placeholder="Silahkan pilih Nas Server"
                    value={params.nas_id}
                    setValue={(selectedId) => {
                      setParams((prev) => ({
                        ...prev,
                        nas_id: selectedId,
                        pop_id: "",
                        odc_id: "",
                        odp_id: "",
                      }))
                    }
                    }
                    searchable={true}
                  />

                  <SelectPop
                    placeholder="Silahkan pilih POP"
                    nas_id={params.nas_id}
                    value={params.pop_id}
                    searchable={true}
                    disabled={!data.nas_id}
                    setValue={(selected) => {
                      setParams((prev) => ({
                        ...prev,
                        pop_id: selected,
                        odc_id: "",
                        odp_id: "",
                      }));
                    }}
                  />

                  <SelectOdc
                    placeholder="Silahkan pilih ODC"
                    pop_id={params.pop_id}
                    value={params.odc_id}
                    searchable
                    disabled={!params.pop_id}
                    setValue={(selectedId) => {
                      setParams((prev) => ({
                        ...prev,
                        odc_id: selectedId,
                        odp_id: "",
                      }));
                    }}
                  />

                  <SelectOdp
                    placeholder="Silahkan pilih ODP"
                    pop_id={params.odc_id}
                    value={params.odp_id}
                    searchable
                    disabled={!params.odc_id}
                    setValue={(selectedId) => {
                      setParams((prev) => ({
                        ...prev,
                        odp_id: selectedId,
                      }));
                    }}
                  />

                </div>
              </div>
            </div>
          </section>

          {/* ===== Desktop Table ===== */}
          <section
            className="hidden lg:block rounded-2xl overflow-hidden"
            style={{
              background: "rgba(15, 23, 42, 0.5)",
              border: "1px solid rgba(51, 65, 85, 0.3)",
            }}
            aria-label="Tabel invoice tagihan"
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                {/* Head */}
                <thead>
                  <tr
                    className="border-b"
                    style={{
                      background: "rgba(15, 23, 42, 0.7)",
                      borderColor: "rgba(51, 65, 85, 0.3)",
                    }}
                  >
                    {/* Checkbox */}
                    <th className="px-4 py-3.5 text-center w-12">
                      <input
                        type="checkbox"
                        checked={
                          selectedIds.length === data?.length && data?.length > 0
                        }
                        onChange={toggleSelectAll}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500/30 focus:ring-offset-0 cursor-pointer"
                      />
                    </th>
                    {columns.map((col) => (
                      <th
                        key={col.key}
                        className={`px-4 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-400 ${col.align === "center" ? "text-center" : "text-left"
                          }`}
                        style={col.width ? { width: col.width } : undefined}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>

                {/* Body */}
                <tbody
                  className="divide-y"
                  style={{ borderColor: "rgba(51, 65, 85, 0.15)" }}
                >
                  {/* Loading */}
                  {loading && !data && (
                    <>
                      {[...Array(5)].map((_, i) => (
                        <SkeletonRow key={i} cols={columns.length + 1} />
                      ))}
                    </>
                  )}

                  {/* Empty */}
                  {!loading && data?.length === 0 && (
                    <tr>
                      <td colSpan={columns.length + 1} className="px-4 py-16 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <div
                            className="w-16 h-16 rounded-2xl flex items-center justify-center"
                            style={{
                              background: "rgba(51, 65, 85, 0.2)",
                              border: "1px solid rgba(51, 65, 85, 0.2)",
                            }}
                          >
                            <FaFileInvoiceDollar className="text-2xl text-slate-600" />
                          </div>
                          <div>
                            <p className="text-slate-400 font-medium">
                              Tidak ada invoice ditemukan
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              {params?.search || params?.nas_id || params?.pop_id || params?.odc_id || params?.odp_id
                                ? "Coba ubah filter atau kata kunci pencarian"
                                : "Belum ada data invoice yang tersedia"}
                            </p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  {/* Data Rows */}
                  {!loading &&
                    data?.map((item, index) => {
                      const sisa = item?.total_tagihan ?? 0;
                      const isLunas = sisa <= 0;

                      return (
                        <tr
                          key={item.id}
                          onClick={() =>
                            navigate(`/dashboard/invoice/detail/${item.id}`)
                          }
                          className="cursor-pointer transition-colors duration-150"
                          style={{ background: "transparent" }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background =
                              "rgba(0, 180, 216, 0.04)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "transparent";
                          }}
                        >
                          {/* Checkbox */}
                          <td className="px-4 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(item.id)}
                              onChange={() => toggleSelect(item.id)}
                              onClick={(e) => e.stopPropagation()}
                              className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500/30 focus:ring-offset-0 cursor-pointer"
                            />
                          </td>

                          {/* No */}
                          <td className="px-4 py-3 text-center text-slate-500 font-mono text-xs">
                            {meta.from + index}
                          </td>

                          {/* Status */}
                          {/* <td className="px-4 py-3 min-w-[160px]">
                            <StatusBadge status={isLunas ? "lunas" : item?.status} />
                          </td> */}

                          {/* ID Pel */}
                          <td className="px-4 py-3">
                            <span className="font-mono text-xs text-cyan-400/80 bg-cyan-500/8 px-2 py-0.5 rounded min-w-[150px]">
                              {item?.internet_number || "-"}
                            </span>
                          </td>

                          {/* Nama */}
                          <td className="px-4 py-3 min-w-[230px]">
                            <div className="flex items-center gap-2">
                              <FaUser className="text-xs text-slate-500" />
                              <span className="font-semibold text-gray-100">
                                {item?.name || "-"}
                              </span>
                            </div>
                          </td>

                          {/* Nominal / Total Tagihan */}
                          <td className="px-4 py-3 min-w-[160px]">
                            <span className="font-bold text-cyan-400">
                              Rp. {formatRupiah(item?.total_bill ?? 0)}
                            </span>
                          </td>

                          {/* Sudah Dibayar */}
                          <td className="px-4 py-3 min-w-[160px]">
                            <span className="font-bold text-emerald-400">
                              Rp. {formatRupiah(item?.total_payment ?? 0)}
                            </span>
                          </td>

                          {/* Sisa Tagihan (Conditional Color) */}
                          <td className="px-4 py-3 min-w-[160px]">
                            <span className={`font-bold text-red-400`}>
                              Rp. {formatRupiah(item?.bill ?? 0)}
                            </span>
                          </td>

                          <td className="px-4 py-3 min-w-[160px]">
                            {item?.pop?.name || "-"}
                          </td>
                          <td className="px-4 py-3 min-w-[160px]">
                            {item?.odc?.name || "-"}
                          </td>
                          <td className="px-4 py-3 min-w-[160px]">
                            {item?.odp?.name || "-"}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {meta && meta.last_page > 1 && (
              <div
                className="flex items-center justify-between px-5 py-3.5"
                style={{ borderTop: "1px solid rgba(51, 65, 85, 0.2)" }}
              >
                <p className="text-xs text-slate-500">
                  Menampilkan{" "}
                  <span className="text-slate-300 font-medium">{meta.from}</span>{" "}
                  sampai{" "}
                  <span className="text-slate-300 font-medium">{meta.to}</span>{" "}
                  dari{" "}
                  <span className="text-slate-300 font-medium">
                    {meta.total}
                  </span>
                </p>

                <nav
                  className="flex items-center gap-1"
                  aria-label="Navigasi halaman"
                >
                  <button
                    onClick={() => setParams((prev) => ({ ...prev, page: 1 }))}
                    disabled={meta.current_page === 1}
                    className="p-2 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-slate-700/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                    aria-label="Halaman pertama"
                  >
                    <FaAnglesLeft className="text-xs" />
                  </button>
                  <button
                    onClick={() => setParams((prev) => ({ ...prev, page: meta.current_page - 1 }))}
                    disabled={meta.current_page === 1}
                    className="p-2 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-slate-700/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                    aria-label="Halaman sebelumnya"
                  >
                    <FaChevronLeft className="text-xs" />
                  </button>

                  {getPages().map((p, i) =>
                    p === "..." ? (
                      <span
                        key={`dot-${i}`}
                        className="px-2 text-slate-600 text-sm"
                      >
                        ···
                      </span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setParams((prev) => ({ ...prev, page: p }))}
                        className={`min-w-[34px] h-9 px-2 rounded-lg text-sm font-medium transition-all duration-200 ${meta.current_page === p
                            ? "bg-cyan-600 text-white shadow-lg shadow-cyan-600/25"
                            : "text-slate-400 hover:text-gray-100 hover:bg-slate-700/40"
                          }`}
                      >
                        {p}
                      </button>
                    )
                  )}

                  <button
                    onClick={() => setParams((prev) => ({ ...prev, page: meta.current_page + 1 }))}
                    disabled={meta.current_page === meta.last_page}
                    className="p-2 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-slate-700/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                    aria-label="Halaman berikutnya"
                  >
                    <FaChevronRight className="text-xs" />
                  </button>
                  <button
                    onClick={() => setParams((prev) => ({ ...prev, page: meta.last_page }))}
                    disabled={meta.current_page === meta.last_page}
                    className="p-2 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-slate-700/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                    aria-label="Halaman terakhir"
                  >
                    <FaAnglesRight className="text-xs" />
                  </button>
                </nav>
              </div>
            )}
          </section>

          {/* ===== Mobile Card List ===== */}
          <section
            className="lg:hidden space-y-3"
            aria-label="Daftar invoice tagihan"
          >
            {/* Loading */}
            {loading && !data && (
              <>
                {[...Array(4)].map((_, i) => (
                  <SkeletonCard key={i} />
                ))}
              </>
            )}

            {/* Empty */}
            {!loading && data?.length === 0 && (
              <div
                className="rounded-2xl p-12 text-center"
                style={{
                  background: "rgba(15, 23, 42, 0.5)",
                  border: "1px solid rgba(51, 65, 85, 0.3)",
                }}
              >
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{
                    background: "rgba(51, 65, 85, 0.2)",
                    border: "1px solid rgba(51, 65, 85, 0.2)",
                  }}
                >
                  <FaFileInvoiceDollar className="text-2xl text-slate-600" />
                </div>
                <p className="text-slate-400 font-medium">
                  Tidak ada invoice ditemukan
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  {params?.search || params?.nas_id || params?.pop_id || params?.odc_id || params?.odp_id
                    ? "Coba ubah filter pencarian"
                    : "Belum ada data invoice"}
                </p>
              </div>
            )}

            {/* Cards */}
            {!loading &&
              data?.map((item) => {
                const sisa = item?.total_tagihan ?? 0;
                const isLunas = sisa <= 0;
                
                return (
                <article
                  key={item.id}
                  onClick={() =>
                    navigate(`/dashboard/invoice/detail/${item.id}`)
                  }
                  className="rounded-2xl overflow-hidden active:scale-[0.98] transition-all duration-200 cursor-pointer"
                  style={{
                    background: "rgba(15, 23, 42, 0.5)",
                    border: "1px solid rgba(51, 65, 85, 0.3)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor =
                      "rgba(0, 180, 216, 0.2)";
                    e.currentTarget.style.background = "rgba(15, 23, 42, 0.7)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor =
                      "rgba(51, 65, 85, 0.3)";
                    e.currentTarget.style.background =
                      "rgba(15, 23, 42, 0.5)";
                  }}
                >
                  {/* Header Card */}
                  <div
                    className="px-4 py-3 flex justify-between items-center"
                    style={{
                      background: "rgba(15, 23, 42, 0.7)",
                      borderBottom: "1px solid rgba(51, 65, 85, 0.2)",
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className="p-1.5 rounded-lg"
                        style={{
                          background: "rgba(0, 180, 216, 0.1)",
                          border: "1px solid rgba(0, 180, 216, 0.15)",
                        }}
                      >
                        <FaHashtag className="text-xs text-cyan-400" />
                      </div>
                      <span className="text-xs font-bold text-slate-300 font-mono">
                        {item?.internet_number || "-"}
                      </span>
                    </div>
                    {/* <StatusBadge status={isLunas ? "lunas" : item?.status} /> */}
                  </div>

                  {/* Konten Card */}
                  <div className="p-4">
                    <div className="flex justify-between items-start mb-4">
                      <div className="space-y-1 flex-1">
                        <h3 className="font-semibold text-gray-100 leading-tight text-sm">
                          {item?.name || "Tidak ada nama"}
                        </h3>
                        <div className="flex items-center gap-1.5 text-slate-500">
                          <FaCreditCard className="text-[11px]" />
                          <span className="text-xs font-medium">
                            Total Tagihan
                          </span>
                        </div>
                      </div>
                      <div className="text-right ml-4">
                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider leading-none mb-1.5">
                          Tagihan
                        </p>
                        <p className="text-lg font-extrabold text-cyan-400">
                          {formatRupiah(item?.total_bill ?? 0)}
                        </p>
                      </div>
                    </div>

                    {/* Detail Pembayaran di Card */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="bg-slate-800/40 rounded-lg p-2.5 border border-slate-700/30">
                        <p className="text-[10px] text-slate-400 uppercase font-semibold mb-1">Dibayar</p>
                        <p className="text-sm font-bold text-emerald-400">
                          {formatRupiah(item?.total_payment ?? 0)}
                        </p>
                      </div>
                      <div className="bg-slate-800/40 rounded-lg p-2.5 border border-slate-700/30">
                        <p className="text-[10px] text-slate-400 uppercase font-semibold mb-1">Sisa</p>
                        <p className={`text-sm font-bold text-red-400`}>
                          {formatRupiah(item?.bill ?? 0)}
                        </p>
                      </div>
                    </div>

                    <div
                      className="flex items-center justify-between pt-3"
                      style={{
                        borderTop: "1px solid rgba(51, 65, 85, 0.15)",
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(item.id)}
                          onChange={() => toggleSelect(item.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500/30 focus:ring-offset-0 cursor-pointer"
                        />
                        <span className="text-xs text-slate-500 font-medium">
                          Pilih
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-cyan-400 font-semibold text-xs">
                        Lihat Detail
                        <FaChevronRight className="text-[10px]" />
                      </div>
                    </div>
                  </div>
                </article>
              )})}
            {/* Mobile Pagination */}
            {meta && meta.last_page > 1 && (
              <div
                className="flex items-center justify-between pt-3 pb-6 px-1"
                style={{ borderTop: "1px solid rgba(51, 65, 85, 0.15)" }}
              >
                <button
                  onClick={() => setParams((prev) => ({ ...prev, page: meta.current_page - 1 }))}
                  disabled={meta.current_page === 1}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  style={{
                    background: "rgba(15, 23, 42, 0.5)",
                    border: "1px solid rgba(51, 65, 85, 0.3)",
                  }}
                >
                  <FaChevronLeft className="text-xs" />
                  Sebelumnya
                </button>

                <span className="text-sm font-medium text-slate-400">
                  <span className="text-cyan-400">{meta.current_page}</span>
                  <span className="text-slate-600 mx-1">/</span>
                  {meta.last_page}
                </span>

                <button
                  onClick={() => setParams((prev) => ({ ...prev, page: meta.current_page + 1 }))}
                  disabled={meta.current_page === meta.last_page}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  style={{
                    background: "rgba(15, 23, 42, 0.5)",
                    border: "1px solid rgba(51, 65, 85, 0.3)",
                  }}
                >
                  Berikutnya
                  <FaChevronRight className="text-xs" />
                </button>
              </div>
            )}
          </section>
        </main>
      </DashboardLayout>
    </>
  );
};